import { loadStripe } from "@stripe/stripe-js";

// Stripe configuration with enhanced development support
export const STRIPE_CONFIG = {
  publishableKey:
    "pk_test_51RKzu6QEBCZXZ5s0guBhQN1OXB8qB3vlYfuutLRc8nRCHYBGI4UttnSEEwj7MeVavHQrHuU6O6EkpBy8mC7h4Rs900XTMc2jQ5",
  options: {
    mode: "setup" as const,
    currency: "usd",
    // Enhanced appearance configuration
    appearance: {
      theme: "stripe" as const,
      variables: {
        colorPrimary: "#0DB17B",
        colorBackground: "#ffffff",
        colorText: "#1f2937",
        colorDanger: "#ef4444",
        fontFamily: "Inter, system-ui, sans-serif",
        spacingUnit: "6px",
        borderRadius: "6px",
      },
      rules: {
        ".Input": {
          padding: "6px 10px",
          fontSize: "13px",
          lineHeight: "18px",
          minHeight: "32px",
        },
        ".Label": {
          fontSize: "12px",
          fontWeight: "500",
          marginBottom: "3px",
        },
        ".Tab": {
          padding: "6px 10px",
          fontSize: "13px",
          minHeight: "32px",
        },
        ".TabIcon": {
          width: "16px",
          height: "16px",
        },
        ".TabLabel": {
          fontSize: "13px",
        },
        ".Error": {
          fontSize: "11px",
          marginTop: "2px",
        },
        ".InputContainer": {
          marginBottom: "8px",
        },
      },
    },
    paymentMethodOrder: ["card"],
    paymentMethodTypes: ["card"],
    paymentMethodCreation: "manual" as const,
    // Development-specific options
    ...(process.env.NODE_ENV === "development" && {
      // Reduce analytics noise in development
      analytics: {
        enabled: false,
      },
    }),
  },
};

// Enhanced Stripe promise with better error handling for development
export const stripePromise = loadStripe(STRIPE_CONFIG.publishableKey)
  .then((stripe) => {
    if (!stripe) {
      throw new Error("Failed to initialize Stripe");
    }

    // Log successful initialization
    if (process.env.NODE_ENV === "development") {
      console.log("✅ Stripe initialized successfully (development mode)");
    }

    return stripe;
  })
  .catch((error) => {
    // Enhanced error logging for development
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "⚠️ Stripe initialization warning (development mode):",
        error.message || error
      );
      console.info(
        "💡 This is normal in development. Stripe analytics errors are expected over HTTP."
      );
    }

    // Return null for graceful degradation
    return null;
  });

// Utility function to check if Stripe is available
export const isStripeAvailable = () => {
  return stripePromise.then((stripe) => !!stripe);
};

// Enhanced utility function to handle Stripe errors gracefully
export const handleStripeError = (error: any): boolean => {
  // Check if this is a development environment
  const isDevelopment = process.env.NODE_ENV === "development";

  // Handle common Stripe analytics errors
  if (
    error?.message?.includes("r.stripe.com") ||
    error?.message?.includes("ERR_BLOCKED_BY_CLIENT") ||
    error?.message?.includes("Failed to fetch") ||
    error?.message?.includes("NetworkError") ||
    error?.message?.includes("ERR_NETWORK")
  ) {
    if (isDevelopment) {
      console.log(
        "🔧 Suppressed Stripe analytics error (development mode):",
        error.message || error
      );
    }
    return true; // Error was handled
  }

  // Log other errors normally
  if (isDevelopment) {
    console.error("❌ Unhandled Stripe error:", error);
  }

  return false; // Error was not handled
};

// Development helper to check Stripe status
export const getStripeStatus = async () => {
  try {
    const stripe = await stripePromise;
    return {
      available: !!stripe,
      mode: process.env.NODE_ENV,
      key: STRIPE_CONFIG.publishableKey.substring(0, 20) + "...",
    };
  } catch (error: any) {
    return {
      available: false,
      mode: process.env.NODE_ENV,
      error: error?.message || "Unknown error",
    };
  }
};
